// Toast placement config — THE single place to change where toasts appear and
// how many are visible at once.
//
// Nothing else in the toast system hardcodes a position: the container styles
// (./styles.ts), the stack order and the enter/exit direction (Toast.tsx) are
// all derived from the `anchor` string below. So flipping `webNarrow` from
// 'bottom-center' to 'top-center' is a one-line change with no other edits.
import { isWeb } from '@common/config/env'
import { BREAKPOINTS } from '@common/modules/dashboard/home-kit/tokens'
import { isExtension } from '@web/constants/browserapi'
import { getUiType } from '@web/utils/uiType'

export type ToastEdge = 'top' | 'bottom'
export type ToastAlign = 'left' | 'center' | 'right'

export type ToastAnchor =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

export type ToastSurfaceId = 'extensionPopup' | 'extensionTab' | 'webWide' | 'webNarrow' | 'native'

export interface ToastSurfaceConfig {
  anchor: ToastAnchor
  maxVisible: number
}

export const TOAST_SURFACE_CONFIG: Record<ToastSurfaceId, ToastSurfaceConfig> = {
  // The popup is ~400px wide and its bottom edge is CTA real estate
  // (FooterGlassView sits there), so toasts stay at the top.
  extensionPopup: { anchor: 'top-center', maxVisible: 1 },
  extensionTab: { anchor: 'top-right', maxVisible: 3 },
  webWide: { anchor: 'top-right', maxVisible: 3 },
  // Phone-sized browsers. Switch to 'top-center' if the bottom placement ever
  // fights with a screen's sticky bottom CTA.
  webNarrow: { anchor: 'bottom-center', maxVisible: 1 },
  // Native keeps the historical placement.
  native: { anchor: 'top-center', maxVisible: 1 }
}

/** Width below which a web surface counts as a phone. Same threshold the mobile
 * bottom tab bar uses, so the toast and the tab bar switch together. */
export const NARROW_BREAKPOINT = BREAKPOINTS.tablet

export const splitAnchor = (anchor: ToastAnchor): [ToastEdge, ToastAlign] => {
  const [edge, align] = anchor.split('-')

  return [edge as ToastEdge, align as ToastAlign]
}

export const resolveToastSurfaceId = (isNarrow: boolean): ToastSurfaceId => {
  if (!isWeb) return 'native'

  const { isPopup, isRequestWindow } = getUiType()

  if (isPopup || isRequestWindow) return 'extensionPopup'
  if (isExtension) return 'extensionTab'

  return isNarrow ? 'webNarrow' : 'webWide'
}
